import { ExternalLink } from "@/components/primitives/external-link";
import { UnitInput } from "@/components/primitives/unit-input";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { calculateSweetenerComposition } from "@/lib/ingredients/form-estimators";
import { useEffect } from "react";
import { UseFormReturn } from "react-hook-form";
import { CreateIngredientFormData } from "./schema";
import { SimpleFormWrapper } from "./simple-wrapper";
export { calculateSweetenerComposition } from "@/lib/ingredients/form-estimators";

type SimpleFormProps = {
  form: UseFormReturn<CreateIngredientFormData>;
};

type SweetenerComposition = Pick<
  CreateIngredientFormData,
  | "sugarsPct"
  | "waterPct"
  | "fatPct"
  | "proteinPct"
  | "carbPct"
  | "fiberPct"
  | "alcoholPct"
  | "energyKcal"
  | "pac"
  | "pod"
>;

const SWEETENER_DERIVED_FIELDS = [
  "sugarsPct",
  "fatPct",
  "proteinPct",
  "carbPct",
  "fiberPct",
  "alcoholPct",
  "energyKcal",
] as const satisfies ReadonlyArray<keyof SweetenerComposition>;

export function SimpleSweetenerForm({ form }: SimpleFormProps) {
  const waterPctValue = form.watch("waterPct");
  const pacValue = form.watch("pac");
  const podValue = form.watch("pod");

  useEffect(() => {
    const calculated = calculateSweetenerComposition(
      waterPctValue,
      pacValue,
      podValue,
    );
    for (const field of SWEETENER_DERIVED_FIELDS) {
      form.setValue(field, calculated[field]);
    }
  }, [waterPctValue, pacValue, podValue, form]);

  return (
    <SimpleFormWrapper form={form} selectedCategory="sweetener">
      <FormField
        control={form.control}
        name="waterPct"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Water %</FormLabel>
            <FormDescription>
              This should be 0 for dry sweeteners. Water content of syrups will
              vary.
            </FormDescription>
            <FormControl>
              <UnitInput {...field} unit="%" min={0} max={100} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="pod"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Relative Sweetness</FormLabel>
            <FormDescription>
              How sweet is this sweetener compared to sucrose?
            </FormDescription>
            <FormControl>
              <UnitInput {...field} unit="%" min={0} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="pac"
        render={({ field }) => (
          <FormItem>
            <FormLabel>FPDF</FormLabel>
            <FormDescription>
              How much does this sweetener lower the freezing point of water,
              when compared to sucrose? <br />
              Not sure how to calculate?{" "}
              <ExternalLink href="/learn/fpdf">Learn more</ExternalLink>
            </FormDescription>
            <FormControl>
              <UnitInput {...field} unit="%" min={0} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </SimpleFormWrapper>
  );
}
